import { useQueryGetAllBots } from "@apis:bot/bot";
import {
  uiPreferencesQueryKey,
  useMutationCreateUiPreference,
  useMutationUpdateUiPreference,
  useQueryGetOneUiPreference,
} from "@apis:ui_preferences/ui_preferences";
import { useQueryGetAllUsers } from "@apis:user/user";
import { Schema } from "@components:SchemaForm";
import { SuperDialog } from "@components:SuperDialog";
import { SuperInput } from "@components:SuperInput/SuperInput";
import { useInvalidateQueries } from "@hooks:useInvalidateQueries";
import { Box, CircularProgress, TextField } from "@mui/material";
import { Preference } from "@pages:settings/Preferences/Preference/Preference";
import { useToasterStoreActions } from "@stores:global";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FormsInputProps } from "./forms";
import uiPreferenceSchema from "./ui_preference_schema.json";

// Module constants so the validator compiled for each schema is reused across renders
const BOT_PREFERENCE_SCHEMA = {
  ...uiPreferenceSchema.$defs.BotPreferenceSchema,
  $defs: uiPreferenceSchema.$defs,
} as Schema;

const USER_PREFERENCE_SCHEMA = {
  ...uiPreferenceSchema.$defs.UserPreferenceSchema,
  $defs: uiPreferenceSchema.$defs,
} as Schema;

interface UiPreferenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  uiPreferenceId?: string;
}

const getDefaultPreference = (isBotPreference: boolean) => {
  if (isBotPreference) {
    // Bot preference: only has streaming configuration
    return {
      isStreaming: null,
    };
  } else {
    // User preference: has bot selection and streaming settings
    return {
      language: null,
      selectedBotId: null,
      botsStreamingEnabled: null,
      botsScrollEnabled: null,
      autoTitleEnabled: null,
      showMessageTokenUsage: null,
    };
  }
};

export const UiPreferenceModal = ({
  isOpen,
  onClose,
  uiPreferenceId,
}: UiPreferenceModalProps) => {
  const { t } = useTranslation();
  const invalidateQueries = useInvalidateQueries();
  const toastActions = useToasterStoreActions();
  const { data: users, isLoading: isUserLoading } = useQueryGetAllUsers();
  const { data: bots, isLoading: isBotLoading } = useQueryGetAllBots(false);

  const userOptions = useMemo(() => {
    return (
      users?.map((user) => ({
        value: user.id,
        label: user.name,
      })) || []
    );
  }, [users]);

  const botOptions = useMemo(() => {
    return (
      bots?.map((bot) => ({
        value: bot.id,
        label: bot.name,
      })) || []
    );
  }, [bots]);

  const { data: uiPreference, isLoading } = useQueryGetOneUiPreference(
    { id: uiPreferenceId || "" },
    { enabled: !!uiPreferenceId },
  );

  const { control, handleSubmit, reset, formState, watch, setValue, trigger } =
    useForm<FormsInputProps>({
      defaultValues: !uiPreferenceId
        ? {
            userId: "",
            botId: "",
            preference: getDefaultPreference(false),
          }
        : undefined,
      values: uiPreference
        ? {
            userId: uiPreference.userId,
            botId: uiPreference.botId || "",
            preference: uiPreference.preference,
          }
        : undefined,
      mode: "all",
    });

  const watchedBotId = watch("botId");
  const isBotPreference = !!watchedBotId;

  useEffect(() => {
    if (uiPreference) {
      reset({
        userId: uiPreference.userId,
        botId: uiPreference.botId || "",
        preference: uiPreference.preference,
      });
    }
  }, [uiPreference, reset]);

  const createMutation = useMutationCreateUiPreference({
    onSuccess: () => {
      toastActions.setToaster({
        open: true,
        variant: "success",
        message: t("preferences.uiPreferences.created"),
      });
    },
    onError: () => {
      toastActions.setToaster({
        open: true,
        variant: "error",
        message: t("preferences.uiPreferences.createFailed"),
      });
    },
  });

  const updateMutation = useMutationUpdateUiPreference({
    onSuccess: () => {
      toastActions.setToaster({
        open: true,
        variant: "success",
        message: t("preferences.uiPreferences.updated"),
      });
    },
    onError: () => {
      toastActions.setToaster({
        open: true,
        variant: "error",
        message: t("preferences.uiPreferences.updateFailed"),
      });
    },
  });

  const onSubmit = async (data: FormsInputProps) => {
    try {
      if (uiPreferenceId) {
        await updateMutation.mutateAsync({
          id: uiPreferenceId,
          preference: data.preference,
        });
      } else {
        const payload = {
          userId: data.userId,
          botId: data.botId || null,
          preference: data.preference,
        };
        await createMutation.mutateAsync(payload);
      }

      await invalidateQueries.run([
        {
          queryKey: uiPreferencesQueryKey._def,
        },
      ]);

      onClose();
    } catch {
      // Error handling is done in mutation callbacks
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const isLoaderShowing = isLoading || isUserLoading || isBotLoading;

  return (
    <SuperDialog
      open={isOpen}
      onClose={onClose}
      title={
        uiPreferenceId
          ? t("preferences.uiPreferences.editTitle")
          : t("preferences.uiPreferences.createTitle")
      }
      isLoading={isLoaderShowing}
      sx={{
        "& .MuiDialog-paper": {
          minWidth: "800px",
          maxWidth: "900px",
        },
      }}
      actionControls={{
        secondary: {
          label: t("common.cancel"),
          onClick: onClose,
          disabled: isSubmitting,
          sx: {
            width: "5rem",
          },
        },
        primary: {
          label: t("common.save"),
          onClick: async () => {
            await handleSubmit(onSubmit)();
          },
          disabled: !formState.isValid || isSubmitting,
          sx: {
            width: "5rem",
          },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Controller
            name="userId"
            rules={{
              required: t("common.requiredField", {
                field: t("preferences.uiPreferences.user"),
              }),
            }}
            control={control}
            render={({ field }) => {
              const selectedValue =
                userOptions.find((option) => option.value === field.value) ??
                null;
              return (
                <SuperInput.Autocomplete
                  disabled={!!uiPreferenceId}
                  sx={{
                    flex: 1,
                  }}
                  clearOnBlur={false}
                  loading={isUserLoading}
                  options={userOptions}
                  isOptionEqualToValue={(option, value) =>
                    option?.value === value?.value
                  }
                  getOptionLabel={(option) => option?.label}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        slotProps={{
                          input: {
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {isUserLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          },
                        }}
                      />
                    );
                  }}
                  {...field}
                  value={selectedValue}
                  onChange={(_, data) => {
                    if (!data) {
                      field.onChange(null);
                      return;
                    }
                    field.onChange(data.value);
                  }}
                  noOptionsText={t("preferences.uiPreferences.noUserFound")}
                  required
                  width="100%"
                  label={t("preferences.uiPreferences.user")}
                  errorText={formState.errors.userId?.message}
                />
              );
            }}
          />

          <Controller
            name="botId"
            control={control}
            render={({ field }) => {
              const selectedValue =
                botOptions.find((option) => option.value === field.value) ??
                null;
              return (
                <SuperInput.Autocomplete
                  disabled={!!uiPreferenceId}
                  sx={{
                    flex: 1,
                  }}
                  clearOnBlur={false}
                  loading={isBotLoading}
                  options={botOptions}
                  isOptionEqualToValue={(option, value) =>
                    option?.value === value?.value
                  }
                  getOptionLabel={(option) => option?.label}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        slotProps={{
                          input: {
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {isBotLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          },
                        }}
                      />
                    );
                  }}
                  {...field}
                  value={selectedValue}
                  onChange={async (_, data) => {
                    if (!data) {
                      field.onChange(null);
                      setValue("preference", getDefaultPreference(false), {
                        shouldDirty: true,
                        shouldValidate: true,
                        shouldTouch: true,
                      });
                      return;
                    }
                    setValue("preference", getDefaultPreference(true), {
                      shouldDirty: true,
                      shouldValidate: true,
                      shouldTouch: true,
                    });
                    field.onChange(data.value);
                  }}
                  noOptionsText={t("preferences.uiPreferences.noBotFound")}
                  width="100%"
                  label={t("preferences.uiPreferences.botOptional")}
                  errorText={formState.errors.botId?.message}
                />
              );
            }}
          />
        </Box>

        <Box sx={{ minHeight: "400px" }}>
          {isBotPreference ? (
            <Preference
              key="bot-preference"
              control={control}
              formState={formState}
              watch={watch}
              reset={reset}
              setValue={setValue}
              trigger={trigger}
              jsonSchema={BOT_PREFERENCE_SCHEMA}
            />
          ) : (
            <Preference
              key="user-preference"
              control={control}
              formState={formState}
              watch={watch}
              reset={reset}
              setValue={setValue}
              trigger={trigger}
              jsonSchema={USER_PREFERENCE_SCHEMA}
            />
          )}
        </Box>
      </Box>
    </SuperDialog>
  );
};
